import cx from 'classnames';
import { Badge } from 'components/Badge';
import { DangerButton, LinkButton, SuccessButton } from 'components/Buttons';
import {
    useApproveApprovalParticipationMutation,
    useWithdrawApprovalItemMutation
} from 'features/comments/approvalApi';
import { documentApi } from 'features/documents/documents';
import { useUserProfile } from 'hooks/useUserProfile';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useGetApprovableTarget } from 'pages/approvals/hooks/useGetApprovableTarget';
import { useGetApprovalItem } from 'pages/approvals/hooks/useGetApprovalItem';
import { useGetApprovalParticipation } from 'pages/approvals/hooks/useGetApprovalParticipation';
import { useGetPurposeAction } from 'pages/approvals/hooks/useGetPurposeAction';
import { useGetPurposeText } from 'pages/approvals/hooks/useGetPurposeText';
import { ApprovalItemType } from 'pages/approvals/modals/ApprovalItems/ApprovalItemType';
import { ApprovalItemParticipations } from 'pages/approvals/views/ApprovalItemParticipations';
import { ApprovalItemMeta } from 'pages/approvals/views/ApprovalItems/ApprovalItemMeta';
import { ApprovalItemTeams } from 'pages/approvals/views/ApprovalItemTeams';
import DocumentHelper from 'pages/global/DocumentHelper';
import HelperFunctions from 'pages/global/HelperFunctions';
import { idToUri } from 'pages/global/UriHelper';
import { useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';
import { Check, ExclamationTriangleFill, InfoSquare, PencilSquare } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

/**
 * @param {string} [approvalItemId]
 * @param {string} [approvalParticipationId]
 * @param {boolean} show
 * @param {function():void} close
 * @param {import('models/approval.models').ApprovalSnapshot} [snapshot]
 */
export function ApprovalItemModal({ approvalItemId, approvalParticipationId, show, close, snapshot }) {
    const approvalItem = useGetApprovalItem(approvalItemId ? idToUri(approvalItemId, 'ApprovalItem') : undefined);

    return (
        <Modal show={show} onHide={close} scrollable={true} size="xl" dialogClassName="dr-modal">
            <Modal.Header closeButton>
                <ModalTitle approvalItem={approvalItem} approvalParticipationId={approvalParticipationId} />
            </Modal.Header>
            <ModalBody
                approvalItem={approvalItem}
                approvalParticipationId={approvalParticipationId}
                snapshot={snapshot}
            />
        </Modal>
    );
}

/**
 * @param {import('models/approval.models').ApprovalItem} [approvalItem]
 * @param {string} [approvalParticipationId]
 */
function ModalTitle({ approvalItem, approvalParticipationId }) {
    const { t } = useTranslation();
    return (
        <Modal.Title>
            <div className="flex-center">
                <div className="mr-3">
                    #{approvalItem?.sequence} - {t('ai:modal.title.browse')}{' '}
                    {approvalParticipationId ? t('ai:modal.title.handle') : ''}
                </div>
                {approvalItem && <StatusBadge approvalItem={approvalItem} />}
            </div>
        </Modal.Title>
    );
}

/**
 * @param {import('models/approval.models').ApprovalItem} [approvalItem]
 * @param {string} [approvalParticipationId]
 * @param {import('models/approval.models').ApprovalSnapshot} [snapshot]
 */
export function ModalBody({ approvalItem, approvalParticipationId, snapshot }) {
    return (
        <Modal.Body className="d-flex p-0">
            <>
                <div className="modal-col-main d-flex flex-column" style={{ flex: 1, minHeight: 420 }}>
                    <div className="overflow-auto p-4" style={{ flex: 1 }}>
                        {approvalItem && <ApprovalItemType approvalItem={approvalItem} snapshot={snapshot} />}
                    </div>

                    <div className="main-footer-wrapper p-4" style={{ borderTop: '1px solid #e2e8f0' }}>
                        <ModalFooter approvalItem={approvalItem} approvalParticipationId={approvalParticipationId} />
                    </div>
                </div>

                <ModalSidebar approvalItem={approvalItem} />
            </>
        </Modal.Body>
    );
}

/**
 * @param {import('models/approval.models').ApprovalItem} [approvalItem]
 * @param {string} [approvalParticipationId]
 */
function ModalFooter({ approvalItem, approvalParticipationId }) {
    if (approvalParticipationId) {
        return (
            <ModalFooterParticipation approvalItem={approvalItem} approvalParticipationId={approvalParticipationId} />
        );
    }

    return <ApprovalItemStatusAlert approvalItem={approvalItem} />;
}

/**
 * @param {import('models/approval.models').ApprovalItem} [approvalItem]
 */
function ApprovalItemStatusAlert({ approvalItem }) {
    const { t } = useTranslation();
    const [withdrawApprovalItem] = useWithdrawApprovalItemMutation();
    const userProfile = useUserProfile();
    const [showWithdraw, setShowWithdraw] = useState(false);

    if (!approvalItem) {
        return null;
    }

    const isOwner = approvalItem?.createdBy === userProfile.userId;

    return <>{renderAlert()}</>;

    function renderAlert() {
        if (approvalItem.state === 'approved') {
            return (
                <Alert variant="success-dark" className="flex-center font-weight-bolder mb-0 px-3">
                    <Check size={16} className="mr-2" /> {t('ai:modal.status.success')}
                </Alert>
            );
        }

        if (approvalItem.state === 'pending') {
            if (showWithdraw) {
                return (
                    <Alert variant="danger" className="flex-center mb-0 px-3">
                        <ExclamationTriangleFill size={16} className="mr-2" /> {t('ai:modal.status.confirmWithdraw')}
                        <div className="ml-auto">
                            <LinkButton size="sm" className="text-dark" onClick={() => setShowWithdraw(false)}>
                                {t('global:btn.cancel')}
                            </LinkButton>
                            <DangerButton onClick={() => withdrawApprovalItem(approvalItem['@id'])} size="sm">
                                {t('ai:modal.status.withdrawConfirm')}
                            </DangerButton>
                        </div>
                    </Alert>
                );
            }

            return (
                <Alert variant="primary" className="flex-center mb-0 px-3">
                    <InfoSquare size={16} className="mr-2" /> {t('ai:modal.status.pending')}
                    {isOwner && (
                        <div className="ml-auto">
                            <LinkButton
                                onClick={() => setShowWithdraw(true)}
                                size="sm"
                                className="text-danger font-weight-bolder p-0"
                            >
                                {t('ai:modal.status.withdrawLink')}
                            </LinkButton>
                        </div>
                    )}
                </Alert>
            );
        }

        if (approvalItem.state === 'withdrawn') {
            return (
                <Alert variant="secondary" className="flex-center mb-0 px-3">
                    <InfoSquare size={16} className="mr-2" /> {t('ai:modal.status.withdrawn')}
                </Alert>
            );
        }
    }
}

/**
 * @param {import('models/approval.models').ApprovalItem} [approvalItem]
 * @param {approvalParticipationId} approvalParticipationId
 */
function ModalFooterParticipation({ approvalItem, approvalParticipationId }) {
    const { t } = useTranslation();
    const participation = useGetApprovalParticipation(idToUri(approvalParticipationId, 'ApprovalParticipation'));
    const userProfile = useUserProfile();

    if (!participation) {
        return null;
    }

    const isCompleted = ['approved', 'completed'].includes(participation.action);

    if (isCompleted) {
        return <RenderApprovedParticipation participation={participation} />;
    }

    if (approvalItem?.state === 'withdrawn') {
        return (
            <Alert variant="secondary" className="flex-center mb-0 px-3">
                <InfoSquare size={16} className="mr-2" /> {t('ai:modal.status.withdrawn')}
            </Alert>
        );
    }

    if (participation.userId !== userProfile.userId) {
        return null;
    }

    return <RenderPendingParticipation approvalItem={approvalItem} participation={participation} />;
}

/**
 * @param {import('models/approval.models').ApprovalItem} [approvalItem]
 * @param {import('models/approval.models').ApprovalParticipation} participation
 */
function RenderPendingParticipation({ approvalItem, participation }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [approveApprovalParticipation] = useApproveApprovalParticipationMutation();
    const [comment, setComment] = useState('');
    const [isSubmitting, setSubmitting] = useState(false);
    const [showComment, setShowComment] = useState(false);
    const target = useGetApprovableTarget(approvalItem?.target?.id);
    const purposeAction = useGetPurposeAction(approvalItem?.purpose, participation.role);

    const [getArea] = documentApi.useLazyGetAreaQuery();
    const [getAreaBlocks] = documentApi.useLazyGetAreaBlocksQuery();
    const [getDocumentVariants] = documentApi.useLazyGetDocumentVariantsQuery();

    return (
        <>
            <div className="flex-center justify-content-between">
                <div>
                    <LinkButton className="flex-center p-0" onClick={() => setShowComment(!showComment)}>
                        <PencilSquare size={12} className="mr-2" style={{ color: '#475569' }} />
                        <span className="small" style={{ color: '#475569' }}>
                            {showComment ? t('ai:modal.comment.hide') : t('ai:modal.comment.add')}
                        </span>
                    </LinkButton>
                </div>
                <SuccessButton
                    onClick={handleApprove}
                    disabled={!target || isSubmitting || approvalItem?.state === 'withdrawn'}
                >
                    {purposeAction}
                </SuccessButton>
            </div>

            <Form.Control
                as="textarea"
                name="comment"
                id="comment"
                maxLength={500}
                rows={3}
                className={cx('mt-3', { 'd-none': !showComment })}
                placeholder={t('ai:modal.comment.placeholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={approvalItem?.state === 'withdrawn'}
            />
        </>
    );

    /**
     * @returns {void}
     */
    function handleApprove() {
        setSubmitting(true);

        getContent().then((content) => {
            approveApprovalParticipation({
                uri: participation['@id'],
                body: {
                    comment,
                    content,
                },
            }).then(() => {
                if (target.type === 'block') {
                    // Invalidate Tags
                    dispatch(
                        documentApi.util.invalidateTags([
                            { type: 'Block', id: target.externalId },
                            { type: 'Area', id: target.parent.externalId },
                        ]),
                    );
                }

                HelperFunctions.sleep(500).then(() => {
                    setSubmitting(false);
                });
            });
        });
    }

    async function getContent() {
        if (approvalItem.target.type === 'block') {
            const area = await getArea(target.parent.externalId).unwrap();
            const documentVariants = await getDocumentVariants(area.documentId).unwrap();
            const baseVariant = documentVariants.find((variant) => variant.parentId === null);
            const areaData = await getAreaBlocks({ areaId: area.id, variantId: baseVariant.id }).unwrap();

            const block = areaData.areaBlocks.find((block) => block.id === parseInt(target.externalId));

            let areaBlocks = [];

            if (block.type === 'blockLayout') {
                const blockIdsInLayout = DocumentHelper.getBlockIdsInLayout([block]);
                areaBlocks = areaData.areaBlocks.filter((block) => blockIdsInLayout.includes(block.id));
            }

            return {
                block,
                areaBlocks,
            };
        }

        return undefined;
    }
}

/**
 * @param {import('models/approval.models').ApprovalParticipation} participation
 */
function RenderApprovedParticipation({ participation }) {
    const { t, i18n } = useTranslation();
    const userProfile = useUserProfile();

    return (
        <div>
            {!isEmpty(participation.comment) && (
                <div>
                    <div
                        className="bg-white px-2 py-1 mb-3"
                        style={{ borderLeft: '3px solid #94a3b8', borderRadius: '0 4px 4px 0' }}
                    >
                        <div className="font-italic" style={{ color: '#475569' }}>
                            &#34;{participation.comment}&#34;
                        </div>
                    </div>
                </div>
            )}

            {renderApprovedDate()}
        </div>
    );

    function renderApprovedDate() {
        if (participation.actedAt) {
            const actedAt = DateTime.fromISO(participation.actedAt).setLocale(i18n.language);

            return (
                <Alert variant="success" className="flex-center mb-0">
                    <Check size={16} className="mr-2 flex-shrink-0" />

                    {participation.userId === userProfile.userId ? (
                        <>
                            {t('ai:modal.status.yourCompleted', {
                                date: actedAt.toLocaleString(DateTime.DATETIME_MED),
                            })}
                        </>
                    ) : (
                        <>{t('ai:modal.status.completed', { date: actedAt.toLocaleString(DateTime.DATETIME_MED) })}</>
                    )}
                </Alert>
            );
        }

        return null;
    }
}

/**
 * @param {import('models/approval.models').ApprovalItem} [approvalItem]
 */
function ModalSidebar({ approvalItem }) {
    const { t } = useTranslation();

    return (
        <div
            className="modal-col-sidebar overflow-auto p-4"
            style={{
                width: 360,
                backgroundColor: '#f1f5f9',
                borderLeft: '1px solid #e2e8f0',
                fontSize: 13,
            }}
        >
            <div
                className="font-weight-bold text-uppercase mb-3"
                style={{
                    borderBottom: '1px solid #cbd5e1',
                    paddingBottom: 4,
                    fontSize: 12,
                    letterSpacing: 0,
                    color: '#475569',
                }}
            >
                {t('ai:modal.sidebar.title')}
            </div>

            {approvalItem && (
                <>
                    <ApprovalItemMeta approvalItem={approvalItem} />
                    <ApprovalItemTeams approvalItem={approvalItem} />
                    <ApprovalItemParticipations approvalItem={approvalItem} />
                </>
            )}
        </div>
    );
}

/**
 * @param {import('models/approval.models').ApprovalItem} approvalItem
 */
function StatusBadge({ approvalItem }) {
    const completed = approvalItem.state === 'approved';
    const purposeText = useGetPurposeText(approvalItem.purpose, completed);

    return <Badge variant={getVariant()}>{purposeText}</Badge>;

    function getVariant() {
        if (approvalItem.state === 'withdrawn') {
            return 'light';
        }

        if (approvalItem.state === 'approved') {
            return 'success';
        }

        return 'warning';
    }
}
